/**
 * Copyright 2024-2026 Tesenso AG
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  SelfRegistrationParams,
  SignUpSelfRegistrationParams,
  SignUpRequest,
  SignUpResponse,
  JwtTokenPair
} from '@shared/models/self-registration/self-registration.models';

@Injectable({
  providedIn: 'root'
})
export class SelfRegistrationService {

  // Admin (JWT-protected) endpoints live under /api/tesenso; public signup-form endpoints
  // live under /api/noauth/tesenso (the only prefix TB CE leaves JWT-free).
  private readonly ADMIN_PREFIX = '/api/tesenso';
  private readonly PUBLIC_PREFIX = '/api/noauth/tesenso';

  constructor(
    private http: HttpClient
  ) {}

  // Admin Methods

  public getSelfRegistrationParams(): Observable<SelfRegistrationParams> {
    return this.http.get<SelfRegistrationParams>(`${this.ADMIN_PREFIX}/selfRegistration/selfRegistrationParams`);
  }

  public saveSelfRegistrationParams(params: SelfRegistrationParams): Observable<SelfRegistrationParams> {
    return this.http.post<SelfRegistrationParams>(`${this.ADMIN_PREFIX}/selfRegistration/selfRegistrationParams`, params);
  }

  public deleteSelfRegistrationParams(domainName: string): Observable<void> {
    return this.http.delete<void>(`${this.ADMIN_PREFIX}/selfRegistration/selfRegistrationParams/${domainName}`);
  }

  // Public Methods (No Auth)

  public getSignUpParams(pkgName?: string): Observable<SignUpSelfRegistrationParams> {
    let params = new HttpParams();
    if (pkgName) {
      params = params.set('pkgName', pkgName);
    }
    return this.http.get<SignUpSelfRegistrationParams>(`${this.PUBLIC_PREFIX}/selfRegistration/signUpSelfRegistrationParams`, { params });
  }

  public getPrivacyPolicy(): Observable<string> {
    return this.http.get(`${this.PUBLIC_PREFIX}/selfRegistration/privacyPolicy`, { responseType: 'text' });
  }

  public getTermsOfUse(): Observable<string> {
    return this.http.get(`${this.PUBLIC_PREFIX}/selfRegistration/termsOfUse`, { responseType: 'text' });
  }

  public signUp(request: SignUpRequest): Observable<SignUpResponse> {
    return this.http.post<SignUpResponse>(`${this.PUBLIC_PREFIX}/signup`, request);
  }

  public activateByEmailCode(emailCode: string, pkgName?: string): Observable<JwtTokenPair> {
    let params = new HttpParams().set('emailCode', emailCode);
    if (pkgName) {
      params = params.set('pkgName', pkgName);
    }
    return this.http.post<JwtTokenPair>(`${this.PUBLIC_PREFIX}/activateByEmailCode`, {}, { params });
  }

  public resendActivationEmail(email: string, pkgName?: string): Observable<void> {
    let params = new HttpParams().set('email', email);
    if (pkgName) {
      params = params.set('pkgName', pkgName);
    }
    return this.http.post<void>(`${this.PUBLIC_PREFIX}/resendEmailActivation`, {}, { params });
  }
}
